import { useEffect, useState } from "react";
import queryString from "query-string";
import { useTranslation } from "react-i18next";
import Layout from "src/components/layout/Layout";
import useAuth from "src/hooks/selectors/useAuth";
import TasksTable from "src/components/tasks/TasksTable";
import TasksGrid from "src/components/tasks/TasksGrid";
import PublishDraftModal from "src/components/forms/TaskForm/elements/PublishDraftModal";
import StudentPoolDropdown from "src/components/form/dropdowns/StudentPoolDropdown";
import useStudentPool from "src/hooks/api/useStudentpool";
import CreateTaskButton from "src/components/forms/TaskForm/elements/CreateTaskButton";
import useTasksView from "src/hooks/useTasksView";
import Tab from "src/components/layout/Tab";
import Tabs from "src/components/layout/Tabs";
import TasksKanban from "src/components/tasks/kanban/TasksKanban";
import Button from "src/components/Button";
import useNavigateModal from "src/hooks/useNavigateModal";
import PublishedTasksCount from "src/components/tasks/PublishedTasksCount";
import useTasksFilter from "src/hooks/api/tasks/useTasksFilter";
import useTenant from "src/hooks/api/services/tenants/useTenant";
import useWindowSize from "src/hooks/useWindowSize";
import useSubscription from "src/hooks/selectors/useSubscription";
import { useParams } from "react-router-dom";

export default function TasksView() {
	const { team } = useParams();
	const { isPhone } = useWindowSize();
	const { tenant } = useTenant();
	const subscription = useSubscription();
	const { view, setView } = useTasksView();
	const { t } = useTranslation("task-view", {
		keyPrefix: "index-view",
	});
	const auth = useAuth();
	const { type } = auth;
	const navigateModal = useNavigateModal();
	const [modalTask, setModalTask] = useState<string | null>(null);
	const { students } = useStudentPool();
	const [matchStudentId, setMatchStudentId] = useState(undefined);
	const { filter, isFiltered, actions } = useTasksFilter();

	useEffect(() => {
		if (team) {
			actions.set({
				teams: [team],
			});
		} else if (filter?.teams?.length) {
			actions.set({
				teams: [],
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [team]);

	return (
		<Layout title={t("title")}>
			{type === "company" ? (
				<div className="flex flex-col flex-1">
					<div className="flex items-center gap-4 flex-wrap justify-between mb-4">
						<div className="flex flex-wrap items-center gap-4">
							{tenant.modules.includes("intern-task") && (
								<Tabs.Container>
									<Tab
										onClick={() => setView("kanban")}
										active={view === "kanban"}
									>
										<i className="far fa-game-board"></i>
									</Tab>
									<Tab
										onClick={() => setView("list")}
										active={view === "list"}
									>
										<i className="fas fa-list-alt"></i>
									</Tab>
								</Tabs.Container>
							)}
							<PublishedTasksCount />
						</div>
						<div className="flex items-center flex-wrap gap-4">
							{isFiltered && (
								<div
									className="cursor-pointer flex gap-2 items-center px-2 transition-all hover:opacity-80"
									onClick={() => actions.clear()}
								>
									<i className="fas fa-times text-sm"></i>
									<span className="font-bold">
										{t("clear-filter")}
									</span>
								</div>
							)}
							<Button
								onClick={() => {
									navigateModal("/tasks/filter");
								}}
								type="gray"
								iconOnly={isPhone}
							>
								<i className="fas fa-filter md:mr-2"></i>
								{!isPhone && <span>{t("filter")}</span>}
							</Button>

							{tenant.modules.includes("intern-task") ? (
								<>
									{subscription?.id && (
										<CreateTaskButton
											iconOnly={isPhone}
											variant="EXTERN"
										/>
									)}
									<CreateTaskButton
										small={isPhone}
										variant="INTERN"
									/>
								</>
							) : (
								<CreateTaskButton variant="CREATE" />
							)}
						</div>
					</div>

					{view === "kanban" && <TasksKanban />}

					{view === "list" && (
						<TasksTable
							{...{ setModalTask }}
							searchActions={
								students?.length > 0 && (
									<>
										<StudentPoolDropdown
											placeholder="Filter op student"
											value={matchStudentId}
											onChange={(value: any) => {
												setMatchStudentId(value);
											}}
										/>
									</>
								)
							}
						/>
					)}
				</div>
			) : (
				<TasksGrid
					q={queryString.stringify({
						recent: 1,
						variant: type === "company" ? "table" : "minimal-list",
					})}
				/>
			)}

			<PublishDraftModal taskId={modalTask} setModalTask={setModalTask} />
		</Layout>
	);
}
